import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  ContentCopy,
  AccountBalance,
  Verified,
  Business,
} from '@mui/icons-material';
import ReceiptUploader from '../components/ReceiptUploader';

// Mock Thaklis bank accounts - TODO: Replace with API call
const thaklisBankAccounts = [
  {
    id: 'bank-001',
    accountName: 'Thaklis - ENBD Main Account',
    bankName: 'Emirates NBD',
    accountNumber: '****7890',
    iban: 'AE070331234567890123456',
    currency: 'AED',
    isActive: true,
    isPrimary: true,
  },
  {
    id: 'bank-002',
    accountName: 'Thaklis - ADCB Business Account',
    bankName: 'Abu Dhabi Commercial Bank',
    accountNumber: '****3456',
    iban: 'AE330321987654321098765',
    currency: 'AED',
    isActive: true,
    isPrimary: false,
  },
  {
    id: 'bank-003',
    accountName: 'Thaklis - DIB Shariah Account',
    bankName: 'Dubai Islamic Bank',
    accountNumber: '****2345',
    iban: 'AE440401234567890123456',
    currency: 'AED',
    isActive: true,
    isPrimary: false,
  },
];

const BankTransferStep = ({
  paymentMethod,
  selectedBankAccount,
  referenceNumber,
  receipt,
  customerNotes,
  onPaymentMethodChange,
  onBankAccountSelect,
  onReferenceNumberChange,
  onReceiptUpload,
  onNotesChange,
}) => {
  const [selectedDestinationBank, setSelectedDestinationBank] = useState(
    selectedBankAccount?.id || thaklisBankAccounts[0]?.id || ''
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // TODO: Add toast notification
  };

  const formatIBAN = (iban) => {
    return iban.match(/.{1,4}/g)?.join(' ') || iban;
  };

  const handleDestinationBankChange = (bankId) => {
    setSelectedDestinationBank(bankId);
    const selectedBank = thaklisBankAccounts.find(acc => acc.id === bankId);
    if (selectedBank) {
      onBankAccountSelect(selectedBank);
    }
  };

  const getSelectedDestinationBank = () => {
    return thaklisBankAccounts.find(acc => acc.id === selectedDestinationBank);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
        Bank Transfer Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please provide your payment details
      </Typography>

      {/* Destination Bank Selection (Where Thaklis Receives Payment) */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(6, 77, 81, 0.05)', border: '2px solid rgba(6, 77, 81, 0.2)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Business sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#064d51' }}>
              Destination Bank Account (Operation)
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which Thaklis bank account should receive your payment
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="destination-bank-label" sx={{ color: '#064d51' }}>
              Select Destination Bank
            </InputLabel>
            <Select
              labelId="destination-bank-label"
              id="destination-bank-select"
              value={selectedDestinationBank}
              label="Select Destination Bank"
              onChange={(e) => handleDestinationBankChange(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(6, 77, 81, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#064d51',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#064d51',
                  },
                },
                '& .MuiSelect-icon': {
                  color: '#064d51',
                },
              }}
            >
              {thaklisBankAccounts.filter(acc => acc.isActive).map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {bank.bankName}
                      {bank.isPrimary && ' (Primary)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {bank.accountName}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Selected Bank Details */}
          {getSelectedDestinationBank() && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', border: '1px solid rgba(6, 77, 81, 0.2)', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#064d51', display: 'block', mb: 0.75 }}>
                ✓ Payment will be sent to:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25 }}>
                {getSelectedDestinationBank().bankName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Account: {getSelectedDestinationBank().accountNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                IBAN: {getSelectedDestinationBank().iban}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payment Method Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              Payment Method *
            </FormLabel>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => onPaymentMethodChange(e.target.value)}
            >
              <FormControlLabel
                value="BANK_TRANSFER"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Bank Transfer</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transfer from your bank account
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="CASH_DEPOSIT"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Cash Deposit</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Deposit cash at bank branch
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1 }}
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Bank Account Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Transfer to Thaklis Bank Account *
        </Typography>
        <Grid container spacing={2}>
          {thaklisBankAccounts.filter(acc => acc.isActive).map((account) => {
            const isSelected = selectedBankAccount?.id === account.id;

            return (
              <Grid item xs={12} key={account.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    }
                  }}
                  onClick={() => onBankAccountSelect(account)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {account.bankName}
                          </Typography>
                          {account.isPrimary && (
                            <Chip
                              label="Primary"
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {account.accountName}
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            IBAN
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                              {formatIBAN(account.iban)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(account.iban);
                              }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Account Number: {account.accountNumber}
                        </Typography>
                      </Box>

                      {isSelected && (
                        <Verified color="primary" sx={{ fontSize: 28 }} />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Instructions */}
      {selectedBankAccount && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Transfer Instructions</AlertTitle>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Transfer the amount to the selected bank account</li>
            <li>Keep your bank receipt or screenshot</li>
            <li>Enter the transaction reference number below</li>
            <li>Upload your receipt or deposit slip</li>
          </ol>
        </Alert>
      )}

      {/* Reference Number */}
      <TextField
        fullWidth
        label="Bank Reference / Transaction Number"
        placeholder="Enter reference number from your receipt"
        value={referenceNumber}
        onChange={(e) => onReferenceNumberChange(e.target.value)}
        required
        helperText="This number is on your bank receipt or transaction confirmation"
        sx={{ mb: 3 }}
      />

      {/* Receipt Upload */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          Upload Receipt / Deposit Slip *
        </Typography>
        <ReceiptUploader
          receipt={receipt}
          onReceiptUpload={onReceiptUpload}
        />
      </Box>

      {/* Optional Notes */}
      <TextField
        fullWidth
        label="Additional Notes (Optional)"
        placeholder="Any additional information about your payment"
        value={customerNotes}
        onChange={(e) => onNotesChange(e.target.value)}
        multiline
        rows={3}
        helperText="e.g., 'Transferred from my Emirates NBD account ending in 1234'"
      />
    </Box>
  );
};

export default BankTransferStep;
